/*
 * AFTER FIVE - af-video-shop.js
 * Native shoppable video: product-page story bubbles ("As seen on": Instagram
 * posts copied into Shopify, plus product videos), collection video reel,
 * sub-collection carousel scroller, muted preview autoplay and the full-screen
 * swipeable player (videos and photos) with product card + add to cart.
 * Plain ES2020, no dependencies. Safe to include several times per page.
 */
(() => {
  'use strict';

  if (window.AfVideoShop) return;

  const doc = document;
  const html = doc.documentElement;

  const SEEN_KEY = 'afVsSeen';
  const SEEN_LIMIT = 300;
  const MUTE_KEY = 'afVsMuted';
  const PREVIEW_RATIO = 0.4;
  // How long one preview plays before the next tile in view takes its turn.
  const PREVIEW_TURN_MS = 8000;
  // How long a row must be in view before its first preview loads.
  const PREVIEW_DWELL_MS = 700;
  // How long the pointer must rest on a card before its preview takes over
  // (a pointer that only crosses the row fetches nothing).
  const PREVIEW_HOVER_MS = 300;
  // In an "all" row the cards start one after another: the next starts when the
  // one before it is on screen, or after PREVIEW_CHAIN_MS at most.
  const PREVIEW_CHAIN_MS = 4000;
  // A preview waits for its own still to be on screen, but no longer than this.
  const PREVIEW_STILL_MS = 2500;
  // A preview asked for once the page has loaded that needs longer than this
  // to come on screen means a slow connection: this page then plays one preview
  // per row. (While the page is still loading every start is slow, so those
  // starts are not counted.)
  const PREVIEW_SLOW_MS = 1800;
  // A playing preview that stops to wait for data for this long: the connection
  // cannot carry what is playing, and the page goes down to one preview per row.
  const PREVIEW_STALL_MS = 1500;
  // Shown by the #af-debug readout, to tell which build a phone is running.
  const BUILD = '2026-10-08b';
  const ACTIVE_RATIO = 0.6;
  const FADE_MS = 220;
  const ADDED_MS = 2000;
  // The player's product card is a slim bar. When there is a colour or a size
  // to choose, its button opens the choices, and the last one picked adds to
  // the cart. false keeps the choices open all the time.
  const CARD_COLLAPSED = true;
  // Photos (Instagram posts) have no duration: each one stays up this long.
  const IMAGE_MS = 5000;
  const IMAGE_WIDTHS = [540, 720, 1080];
  const IMAGE_SIZES = '(min-width: 750px) 520px, 100vw';
  // Photos at least this tall (width / height) fill the screen like a video.
  const COVER_ASPECT = 0.62;
  // Taps work like a story: the left part of the screen goes back, the rest
  // goes forward; holding for PRESS_MS pauses until release.
  const BACK_ZONE = 0.3;
  const PRESS_MS = 250;

  // English labels. Where the theme has translations, Liquid prints them
  // (snippets/af-vs-strings.liquid) and they replace these.
  const TEXT = {
    add: 'Add to cart',
    preorder: 'Pre-order',
    soldOut: 'Sold out',
    selectOption: 'Select [option]',
    adding: 'Adding…',
    added: 'Added ✓',
    error: 'Something went wrong. Please try again.',
    dialog: 'Shop the look',
    close: 'Close',
    mute: 'Mute',
    unmute: 'Unmute',
    prev: 'Previous',
    next: 'Next',
    view: 'View product',
    pause: 'Pause',
    play: 'Play',
    slideOf: '[current] of [total]',
    instagram: 'Instagram',
    viewPost: 'view post on Instagram',
    pausePreviews: 'Pause videos',
    playPreviews: 'Play videos',
  };

  (() => {
    const node = doc.querySelector('script[data-af-vs-strings]');
    if (!node) return;
    try {
      const strings = JSON.parse(node.textContent || '{}');
      Object.keys(TEXT).forEach((key) => {
        const value = typeof strings[key] === 'string' ? decodeEntities(strings[key]).trim() : '';
        if (value) TEXT[key] = value;
      });
    } catch (e) {
      /* keep the English labels */
    }
  })();

  // The store's icon family (snippets/af-icon.liquid in the redesigned theme):
  // outline glyphs on a 24px grid, 1.5px stroke, round caps and joins.
  const svg = (body, size) =>
    `<svg viewBox="0 0 24 24" width="${size || 24}" height="${size || 24}" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">${body}</svg>`;

  const ICONS = {
    close: svg('<path d="M5 5l14 14M19 5L5 19"/>'),
    sound: svg('<path d="M4 9.5h3.5L12 6v12l-4.5-3.5H4z"/><path d="M15.5 9a4 4 0 010 6M17.8 6.6a7.4 7.4 0 010 10.8"/>'),
    muted: svg('<path d="M4 9.5h3.5L12 6v12l-4.5-3.5H4z"/><path d="M15.5 9.5l5 5M20.5 9.5l-5 5"/>'),
    prev: svg('<path d="M15 6l-6 6 6 6"/>'),
    next: svg('<path d="M9 6l6 6-6 6"/>'),
    play: svg('<path d="M8 5.5v13l10.5-6.5L8 5.5z"/>', 28),
    pause: svg('<path d="M9 6v12M15 6v12"/>'),
    resume: svg('<path d="M8 5.5v13l10.5-6.5L8 5.5z"/>'),
    instagram: svg(
      '<rect x="3.75" y="3.75" width="16.5" height="16.5" rx="4.5"/><circle cx="12" cy="12" r="3.75"/><path d="M17 7h.01"/>',
      20
    ),
  };

  /* ------------------------------------------------------------------ */
  /* Generic helpers                                                     */
  /* ------------------------------------------------------------------ */

  const mq = (query) => (typeof window.matchMedia === 'function' ? window.matchMedia(query) : null);
  const reducedMotionMq = mq('(prefers-reduced-motion: reduce)');
  const reducedMotion = () => !!(reducedMotionMq && reducedMotionMq.matches);
  const saveData = () => !!(navigator.connection && navigator.connection.saveData);
  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
  const quiet = (promise) => {
    if (promise && typeof promise.then === 'function') promise.catch(() => {});
    return promise;
  };
  const onMqChange = (list, fn) => {
    if (!list) return;
    if (typeof list.addEventListener === 'function') list.addEventListener('change', fn);
    else if (typeof list.addListener === 'function') list.addListener(fn);
  };
  const hasInert = typeof HTMLElement !== 'undefined' && 'inert' in HTMLElement.prototype;
  // Liquid only exposes the top-quality MP4 (often 1080p) plus Shopify's HLS
  // stream. Native HLS (Safari/iOS, Chrome) adapts to bandwidth and player size,
  // so prefer it; fall back to the MP4 where HLS is unsupported or fails.
  const canHls = (() => {
    try {
      return !!doc.createElement('video').canPlayType('application/vnd.apple.mpegurl');
    } catch (e) {
      return false;
    }
  })();
  // "/en-au/collections/x/products/y" and "/products/y" are the same product page.
  const productPath = (path) => {
    const at = String(path || '').indexOf('/products/');
    return (at === -1 ? String(path || '') : String(path).slice(at)).replace(/\/$/, '');
  };

  function create(tag, className, attrs) {
    const node = doc.createElement(tag);
    if (className) node.className = className;
    if (attrs) {
      Object.keys(attrs).forEach((key) => {
        const value = attrs[key];
        if (value === false || value === null || value === undefined) return;
        node.setAttribute(key, value === true ? '' : String(value));
      });
    }
    return node;
  }

  function decodeEntities(value) {
    const str = value == null ? '' : String(value);
    if (str.indexOf('&') === -1) return str;
    const area = doc.createElement('textarea');
    area.innerHTML = str;
    return area.value;
  }

  function storageGet(store, key) {
    try {
      return window[store].getItem(key);
    } catch (e) {
      return null;
    }
  }

  function storageSet(store, key, value) {
    try {
      window[store].setItem(key, value);
    } catch (e) {
      /* storage blocked or full: the feature simply does not persist */
    }
  }

  /* ------------------------------------------------------------------ */
  /* "Seen" state for story bubbles (localStorage afVsSeen)              */
  /* ------------------------------------------------------------------ */

  let seenIds = null;

  function getSeen() {
    if (seenIds) return seenIds;
    seenIds = [];
    try {
      const parsed = JSON.parse(storageGet('localStorage', SEEN_KEY) || '[]');
      if (Array.isArray(parsed)) seenIds = parsed.map(String);
    } catch (e) {
      seenIds = [];
    }
    return seenIds;
  }

  function applySeen(scope) {
    const seen = getSeen();
    if (!seen.length || !scope) return;
    const set = new Set(seen);
    scope.querySelectorAll('.af-vs-bubble[data-af-vs-media]').forEach((bubble) => {
      if (set.has(bubble.getAttribute('data-af-vs-media'))) bubble.classList.add('is-seen');
    });
  }

  function markSeen(id) {
    if (id === null || id === undefined || id === '') return;
    const key = String(id);
    const list = getSeen();
    if (list.indexOf(key) === -1) {
      list.push(key);
      if (list.length > SEEN_LIMIT) list.splice(0, list.length - SEEN_LIMIT);
      const stored = list.map((x) => (/^\d{1,15}$/.test(x) ? Number(x) : x));
      storageSet('localStorage', SEEN_KEY, JSON.stringify(stored));
    }
    doc.querySelectorAll('.af-vs-bubble[data-af-vs-media]').forEach((bubble) => {
      if (bubble.getAttribute('data-af-vs-media') === key) bubble.classList.add('is-seen');
    });
  }

  /* ------------------------------------------------------------------ */
  /* Payload                                                             */
  /* ------------------------------------------------------------------ */

  function getPayload(root) {
    if (!root) return null;
    if (root._afVsPayload !== undefined) return root._afVsPayload;
    let payload = null;
    const script = root.querySelector('script[data-af-vs-payload]');
    if (script) {
      try {
        const data = JSON.parse(script.textContent || '{}');
        const items = data && Array.isArray(data.items) ? data.items : [];
        const products = data && data.products && typeof data.products === 'object' ? data.products : {};
        const posts = data && data.posts && typeof data.posts === 'object' ? data.posts : {};
        if (items.length) payload = { items, products, posts };
      } catch (e) {
        payload = null;
      }
    }
    root._afVsPayload = payload;
    return payload;
  }

  /* ------------------------------------------------------------------ */
  /* Horizontal scroller (tracks + prev/next arrows)                     */
  /* ------------------------------------------------------------------ */

  function initScroller(scroller, root) {
    if (!scroller) return null;
    if (scroller._afVsScroller) return scroller._afVsScroller;
    const track = scroller.querySelector('[data-af-vs-track]');
    if (!track) return null;
    // The arrows may sit outside the scroll area, in the widget's heading row.
    const scope = root && root.contains(scroller) ? root : scroller;
    const prev = scope.querySelector('[data-af-vs-prev]');
    const next = scope.querySelector('[data-af-vs-next]');
    const nav = prev && prev.parentElement && prev.parentElement.classList.contains('af-vs-nav') ? prev.parentElement : null;
    let frame = 0;
    let resizeObserver = null;

    const update = () => {
      frame = 0;
      const max = track.scrollWidth - track.clientWidth;
      const overflowing = max > 4;
      const pos = Math.abs(track.scrollLeft);
      scroller.classList.toggle('is-overflowing', overflowing);
      if (nav) nav.hidden = !overflowing;
      if (prev) {
        prev.hidden = !overflowing;
        prev.disabled = !overflowing || pos <= 4;
      }
      if (next) {
        next.hidden = !overflowing;
        next.disabled = !overflowing || pos >= max - 4;
      }
      // Where the row's first card starts (rows that fit are centred): the
      // pause button sits on it, or on the row's edge once it is scrolled away.
      const first = track.firstElementChild;
      if (first) {
        const pad = parseFloat(getComputedStyle(track).paddingLeft) || 0;
        const start = Math.max(first.offsetLeft - track.scrollLeft, track.offsetLeft + pad);
        scroller.style.setProperty('--af-vs-row-start', `${Math.round(start)}px`);
      }
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    const step = (direction) => {
      const rtl = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
      const distance = Math.max(track.clientWidth * 0.9, 1) * direction * rtl;
      const behavior = reducedMotion() ? 'auto' : 'smooth';
      try {
        track.scrollBy({ left: distance, behavior });
      } catch (e) {
        track.scrollLeft += distance;
      }
    };
    const onPrev = (event) => {
      event.preventDefault();
      step(-1);
    };
    const onNext = (event) => {
      event.preventDefault();
      step(1);
    };

    track.addEventListener('scroll', schedule, { passive: true });
    if (prev) prev.addEventListener('click', onPrev);
    if (next) next.addEventListener('click', onNext);
    if (typeof ResizeObserver === 'function') {
      resizeObserver = new ResizeObserver(schedule);
      resizeObserver.observe(track);
    } else {
      window.addEventListener('resize', schedule);
    }
    if (doc.readyState !== 'complete') window.addEventListener('load', schedule, { once: true });
    schedule();

    const api = {
      update: schedule,
      destroy() {
        track.removeEventListener('scroll', schedule);
        window.removeEventListener('load', schedule);
        if (prev) prev.removeEventListener('click', onPrev);
        if (next) next.removeEventListener('click', onNext);
        if (resizeObserver) resizeObserver.disconnect();
        else window.removeEventListener('resize', schedule);
        if (frame) cancelAnimationFrame(frame);
        frame = 0;
        if (scroller._afVsScroller === api) scroller._afVsScroller = null;
      },
    };
    scroller._afVsScroller = api;
    return api;
  }

  /* ------------------------------------------------------------------ */
  /* One Tab stop per row of bubbles or tiles; arrow keys move inside it  */
  /* ------------------------------------------------------------------ */

  function initRoving(track) {
    if (!track) return null;
    if (track._afVsRoving) return track._afVsRoving;
    const stops = () => Array.from(track.querySelectorAll('[data-af-vs-open]'));
    if (stops().length < 2) return null;

    const setStop = (active) => {
      stops().forEach((stop) => stop.setAttribute('tabindex', stop === active ? '0' : '-1'));
    };
    // Bring the whole bubble or tile into the row's visible part.
    const reveal = (stop) => {
      const item = stop.closest('li') || stop;
      const box = track.getBoundingClientRect();
      const rect = item.getBoundingClientRect();
      let delta = 0;
      if (rect.left < box.left + 4) delta = rect.left - box.left - 4;
      else if (rect.right > box.right - 4) delta = rect.right - box.right + 4;
      if (!delta) return;
      try {
        track.scrollBy({ left: delta, behavior: reducedMotion() ? 'auto' : 'smooth' });
      } catch (e) {
        track.scrollLeft += delta;
      }
    };
    const onKeydown = (event) => {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      const list = stops();
      const at = list.indexOf(doc.activeElement);
      if (at === -1) return;
      const forward = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
      let to;
      if (event.key === 'ArrowRight') to = at + forward;
      else if (event.key === 'ArrowLeft') to = at - forward;
      else if (event.key === 'Home') to = 0;
      else if (event.key === 'End') to = list.length - 1;
      else return;
      event.preventDefault();
      const target = list[clamp(to, 0, list.length - 1)];
      if (target && target !== list[at]) target.focus({ preventScroll: true });
    };
    const onFocusin = (event) => {
      const stop = event.target instanceof Element ? event.target.closest('[data-af-vs-open]') : null;
      if (!stop || !track.contains(stop)) return;
      setStop(stop);
      reveal(stop);
    };

    setStop(stops()[0]);
    track.addEventListener('keydown', onKeydown);
    track.addEventListener('focusin', onFocusin);
    const api = {
      destroy() {
        track.removeEventListener('keydown', onKeydown);
        track.removeEventListener('focusin', onFocusin);
        stops().forEach((stop) => stop.removeAttribute('tabindex'));
        if (track._afVsRoving === api) track._afVsRoving = null;
      },
    };
    track._afVsRoving = api;
    return api;
  }

  /* ------------------------------------------------------------------ */
  /* The reel's place in the product grid                                */
  /* ------------------------------------------------------------------ */

  // Where the grid prints the reel once, as
  // li.af-vs-reel-item[data-af-vs-reel-rows="N"] with grid-row N + 1, it is
  // drawn after N rows of products at every column count. The script keeps its
  // place in the page code (reading and Tab order) in step with that, and moves
  // it up when the collection has fewer than N rows.
  let reelFrame = 0;
  let reelGridObserver = null;

  function placeReel(item) {
    const grid = item.parentElement;
    const rows = parseInt(item.getAttribute('data-af-vs-reel-rows'), 10);
    if (!grid || !(rows > 0)) return;
    const template = getComputedStyle(grid).gridTemplateColumns || '';
    // Not a laid-out CSS grid (hidden, or a theme without the grid): leave it.
    if (!template || template === 'none' || template.indexOf('(') !== -1) return;
    const cols = template.split(' ').length;
    const cards = Array.from(grid.children).filter((child) => !child.classList.contains('af-vs-reel-item'));
    const above = Math.min(rows, Math.ceil(cards.length / cols));
    const row = String(above + 1);
    if (item.style.gridRow !== row && item.style.gridRowStart !== row) item.style.gridRow = row;
    const before = cards[above * cols] || null;
    if (item.nextElementSibling !== before) grid.insertBefore(item, before);
    if (typeof ResizeObserver === 'function' && !grid._afVsReelWatched) {
      // A change of column count (screen size, the density switch) changes the grid's size.
      grid._afVsReelWatched = true;
      if (!reelGridObserver) reelGridObserver = new ResizeObserver(scheduleReels);
      reelGridObserver.observe(grid);
    }
  }

  function placeReels() {
    reelFrame = 0;
    doc.querySelectorAll('li.af-vs-reel-item[data-af-vs-reel-rows]').forEach(placeReel);
  }

  function scheduleReels() {
    if (!reelFrame) reelFrame = requestAnimationFrame(placeReels);
  }

  /* ------------------------------------------------------------------ */
  /* Preview autoplay: video[data-af-vs-autoplay]                        */
  /* ------------------------------------------------------------------ */

  // When a preview starts. Its file is requested only when its turn comes: the
  // page must be usable (parsed; the script itself is deferred), the row must
  // have been in view for PREVIEW_DWELL_MS, so a shopper who scrolls straight
  // past fetches nothing, and the preview's own still must be showing. It does
  // NOT wait for the window's load event: on a phone on mobile data, scripts
  // from other services can hold that back for 20 seconds, and nothing played
  // until then.
  // What a row plays is its data-af-vs-previews mode. "all" (the collection
  // carousel and the reel): every card or tile in view, up to
  // maxPreviewCards(), started one after another so the files never compete
  // with each other. "turns": ONE preview at a time, the tiles in view taking
  // turns. "first": only the first card in view, until the shopper swipes the
  // row or points at another card. A phone plays one row at a time, wider
  // screens two. The 480p file is used when the theme provides it. A pause
  // button on each row stops them all on this page. Save-Data and 2G: no
  // previews. A device that asks for reduced motion still gets them (owner's
  // decision, 5 October 2026: her own phone had Reduce Motion on, and the store
  // showed it no moving video at all); the pause button is what such a visitor
  // uses. On a slow connection (the browser says so, a preview
  // asked for after the page had loaded took more than PREVIEW_SLOW_MS to come
  // on screen, or a playing one stalled) a row plays one preview only. A phone
  // that refuses to start a video by itself (iOS in Low Power Mode) keeps the
  // stills until the first tap on the page.
  // The first preview of a row that is on screen when the page arrives is
  // started by the row itself (snippets/af-vs-early.liquid), seconds before
  // this file runs: registerPreview() takes it over as it is.

  const previews = new Set();
  // Row (the widget element) -> { row, mode, videos, current, waitingFor, hover, timer, seenAt, button }.
  const previewGroups = new Map();
  // Rows with a preview in view, most recently scrolled into view first.
  let previewOrder = [];
  let previewObserver = null;
  let previewFrame = 0;
  let previewDwellTimer = 0;
  // The pause button: for this page only. (Kept for a whole visit, one stray tap
  // on it left every page without motion and nothing to say why.)
  let previewsOff = false;
  // The browser refused to start a preview by itself.
  let previewsRefused = false;
  // True while previews are being started inside a tap (see onPreviewRefused).
  let previewBurst = false;
  // A preview was slow to start on this page. Safari says nothing about the
  // connection, so the previews' own start time is the measure.
  let previewSlow = false;
  let previewSlowWhy = '';
  const markSlow = (why) => {
    if (previewSlow) return;
    previewSlow = true;
    previewSlowWhy = why;
    schedulePreviews();
  };

  const slowLink = () => {
    const type = navigator.connection && navigator.connection.effectiveType;
    return typeof type === 'string' && /2g$/.test(type);
  };
  const previewsPossible = () => !saveData() && !slowLink();
  const previewsAllowed = () => !previewsOff && previewsPossible();
  const previewsHeld = () => doc.hidden || player.isOpen();
  const maxPreviewRows = () => (window.innerWidth < 750 ? 1 : 2);
  // Cards an "all" row plays at once. A slow connection gets the first one only.
  const maxPreviewCards = (group) => {
    const link = navigator.connection;
    const slow =
      link && (link.effectiveType === '3g' || (typeof link.downlink === 'number' && link.downlink > 0 && link.downlink < 1.5));
    if (slow || previewSlow) return 1;
    if (window.innerWidth >= 750) return 6;
    // A phone shows two cards or tiles, and four bubbles with a fifth coming
    // into view: every bubble in view plays (owner, 8 October 2026).
    return group && group.bubbles ? 5 : 2;
  };
  const previewMode = (row) => {
    const mode = row.getAttribute('data-af-vs-previews');
    return mode === 'first' || mode === 'all' ? mode : 'turns';
  };
  const inViewOf = (group) => group.videos.filter((video) => video._afVsInView && video.isConnected);

  function getPreviewObserver() {
    if (previewObserver || typeof IntersectionObserver !== 'function') return previewObserver;
    previewObserver = new IntersectionObserver(
      (entries) => {
        const touched = new Set();
        entries.forEach((entry) => {
          const video = entry.target;
          const group = video._afVsGroup;
          const wasInView = !!video._afVsInView;
          video._afVsInView = entry.isIntersecting && entry.intersectionRatio >= PREVIEW_RATIO;
          if (!group) return;
          touched.add(group);
          // A row that has just come into view goes to the front of the queue.
          if (video._afVsInView && !wasInView && previewOrder[0] !== group) {
            previewOrder = [group].concat(previewOrder.filter((other) => other !== group));
          }
        });
        // The wait before a row's first preview runs from when it came into view.
        touched.forEach((group) => {
          if (!inViewOf(group).length) group.seenAt = 0;
          else if (!group.seenAt) group.seenAt = performance.now();
        });
        schedulePreviews();
      },
      { threshold: [0, PREVIEW_RATIO] }
    );
    return previewObserver;
  }

  // Resting the mouse on a card or tile plays its preview, without the row's wait.
  function onPreviewPointerOver(group, event) {
    if (event.pointerType !== 'mouse') return;
    const tile = event.target instanceof Element ? event.target.closest('.af-vs-tile, .af-vs-cc__card') : null;
    const video = tile ? tile.querySelector('video[data-af-vs-autoplay]') : null;
    const pointed = video && video._afVsGroup === group ? video : null;
    if (pointed === group.pointed) return;
    group.pointed = pointed;
    clearTimeout(group.hoverTimer);
    if (!pointed) {
      if (group.hover) {
        group.hover = null;
        schedulePreviews();
      }
      return;
    }
    group.hoverTimer = setTimeout(() => {
      if (group.pointed !== pointed) return;
      group.hover = pointed;
      if (group.seenAt) group.seenAt = performance.now() - PREVIEW_DWELL_MS;
      schedulePreviews();
    }, PREVIEW_HOVER_MS);
  }

  function previewGroupOf(video) {
    const row = video.closest('af-vs-reel, af-vs-scroller, af-vs-stories, [data-af-vs-root]') || doc.body;
    let group = previewGroups.get(row);
    if (!group) {
      group = {
        row,
        mode: previewMode(row),
        bubbles: row !== doc.body && row.matches('af-vs-stories'),
        videos: [],
        current: null,
        // "all" rows: the card the ones after it are waiting for.
        waitingFor: null,
        // The card the mouse is on, and the one it has rested on long enough.
        pointed: null,
        hover: null,
        hoverTimer: 0,
        timer: 0,
        seenAt: 0,
        button: null,
      };
      group.onOver = (event) => onPreviewPointerOver(group, event);
      group.onLeave = () => {
        clearTimeout(group.hoverTimer);
        group.pointed = null;
        if (!group.hover) return;
        group.hover = null;
        schedulePreviews();
      };
      if (row !== doc.body) {
        row.addEventListener('pointerover', group.onOver);
        row.addEventListener('pointerleave', group.onLeave);
      }
      previewGroups.set(row, group);
    }
    return group;
  }

  // True while a preview that has been on screen is out of data.
  const starving = (video) => !video.paused && video.classList.contains('is-ready') && video.readyState <= 2;

  function watchStall(video) {
    if (!video.classList.contains('is-ready')) return;
    clearTimeout(video._afVsStallTimer);
    video._afVsStallTimer = setTimeout(() => {
      if (!starving(video)) return;
      video._afVsStalls = (video._afVsStalls || 0) + 1;
      markSlow('stalled');
    }, PREVIEW_STALL_MS);
  }

  function registerPreview(video) {
    if (!video || previews.has(video)) return;
    previews.add(video);
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    if (!video._afVsErrorBound) {
      video._afVsErrorBound = true;
      // The still under the video shows until the first frame is on screen.
      video.addEventListener('playing', () => {
        clearTimeout(video._afVsStallTimer);
        video._afVsRefused = '';
        if (!video.classList.contains('is-ready')) {
          const now = performance.now();
          if (!video._afVsShownAt) video._afVsShownAt = now;
          if (video._afVsAskedAt && !video._afVsAskedBusy && now - video._afVsAskedAt > PREVIEW_SLOW_MS) markSlow('slow start');
        }
        video.classList.add('is-ready');
        // In an "all" row the next card was waiting for this one.
        if (video._afVsGroup && video._afVsGroup.mode === 'all') schedulePreviews();
      });
      // A preview that was playing and has run out of data. (A loop going back
      // to its start also says "waiting", for a few milliseconds.)
      video.addEventListener('waiting', () => watchStall(video));
      video.addEventListener('pause', () => clearTimeout(video._afVsStallTimer));
      video.addEventListener('error', () => {
        const current = video.getAttribute('src');
        if (!current) return;
        // This file failed here: try the other kind once (MP4 <-> stream).
        if (current === video.getAttribute('data-src') && !video._afVsMp4Failed) video._afVsMp4Failed = true;
        else if (current === video.getAttribute('data-hls') && !video._afVsHlsFailed) video._afVsHlsFailed = true;
        else return;
        const group = video._afVsGroup;
        const wanted = group && (group.current === video || (group.mode === 'all' && video._afVsInView));
        if (wanted && previewSrc(video) !== current) startPreview(video);
      });
    }
    const group = previewGroupOf(video);
    video._afVsGroup = group;
    group.videos.push(video);
    if (video._afVsEarly) {
      video._afVsAskedAt = video._afVsEarly;
      video._afVsAskedBusy = true;
      video._afVsStillDone = true;
      if (video._afVsEarlyError) video._afVsRefused = video._afVsEarlyError;
      if (!video.paused) {
        // Already asked for, perhaps already playing: the row counts as seen and
        // past its wait, so the first look at it leaves the video running.
        if (video.readyState > 2 && video.currentTime > 0) {
          video.classList.add('is-ready');
          if (!video._afVsShownAt) video._afVsShownAt = performance.now();
        }
        // It may have run out of data before this file was there to hear it.
        if (starving(video)) watchStall(video);
        video._afVsInView = true;
        if (!group.seenAt) group.seenAt = performance.now() - PREVIEW_DWELL_MS;
        if (previewOrder.indexOf(group) === -1) previewOrder.unshift(group);
      }
    }
    ensureMotionButton(group);
    const observer = getPreviewObserver();
    if (observer) observer.observe(video);
  }

  function unregisterPreview(video) {
    if (!video || !previews.delete(video)) return;
    if (previewObserver) previewObserver.unobserve(video);
    video._afVsInView = false;
    stopPreview(video);
    const group = video._afVsGroup;
    video._afVsGroup = null;
    if (!group) return;
    group.videos = group.videos.filter((other) => other !== video);
    if (group.current === video) group.current = null;
    if (group.waitingFor === video) group.waitingFor = null;
    if (group.hover === video) group.hover = null;
    if (group.pointed === video) group.pointed = null;
    if (!group.videos.length) {
      clearTimeout(group.timer);
      clearTimeout(group.hoverTimer);
      if (group.button) group.button.remove();
      group.row.removeEventListener('pointerover', group.onOver);
      group.row.removeEventListener('pointerleave', group.onLeave);
      previewGroups.delete(group.row);
      previewOrder = previewOrder.filter((other) => other !== group);
    }
    schedulePreviews();
  }

  function previewSrc(video) {
    const mp4 = video._afVsMp4Failed ? '' : video.getAttribute('data-src') || '';
    const hls = canHls && !video._afVsHlsFailed ? video.getAttribute('data-hls') || '' : '';
    // A 480p (or smaller) MP4 is the lightest file for a small tile. Without
    // one the stream is next best: it adapts instead of forcing the largest MP4.
    if (mp4 && /-(?:480|360)p[-.]/.test(mp4)) return mp4;
    return hls || mp4;
  }

  // A preview starts once its own still is showing, so the photo the shopper
  // sees first is never held up by the video.
  function stillReady(video) {
    if (video._afVsStillDone) return true;
    const still = video.parentNode ? video.parentNode.querySelector('img.af-vs-cc__img, img.af-vs-tile__poster, img.af-vs-bubble__img') : null;
    if (!still || (still.complete && still.naturalWidth > 0)) {
      video._afVsStillDone = true;
      return true;
    }
    if (!video._afVsStillWait) {
      video._afVsStillWait = true;
      const done = () => {
        if (video._afVsStillDone) return;
        video._afVsStillDone = true;
        schedulePreviews();
      };
      still.addEventListener('load', done, { once: true });
      still.addEventListener('error', done, { once: true });
      setTimeout(done, PREVIEW_STILL_MS);
    }
    return false;
  }

  // Some phones refuse to start a video by themselves (iOS in Low Power Mode).
  // The stills stay, the pause button steps aside, and the first tap or key
  // press anywhere on the page, which the browser does accept, starts them.
  function onPreviewRefused() {
    if (previewsRefused) return;
    previewsRefused = true;
    updateMotionButtons();
    const events = ['touchend', 'pointerup', 'click', 'keydown'];
    const retry = () => {
      events.forEach((type) => doc.removeEventListener(type, retry, true));
      previewsRefused = false;
      updateMotionButtons();
      // Inside the tap itself, not a frame later: that is what the browser
      // accepts. Touching every preview here also lets each of them start by
      // itself later on (the browser lifts its refusal per video).
      previews.forEach((video) => {
        if (!video.paused) return;
        try {
          quiet(video.play());
          video.pause();
        } catch (e) {
          /* ignore */
        }
      });
      // And the ones in view start now, all at once, without waiting their turn.
      previewBurst = true;
      runPreviews();
      previewBurst = false;
    };
    events.forEach((type) => doc.addEventListener(type, retry, true));
  }

  function startPreview(video) {
    if (previewsHeld() || !previewsAllowed() || !stillReady(video)) return;
    const src = previewSrc(video);
    if (!src) return;
    if (video.getAttribute('src') !== src) video.setAttribute('src', src);
    video.muted = true;
    if (!video.classList.contains('is-ready')) {
      video._afVsAskedAt = performance.now();
      video._afVsAskedBusy = doc.readyState !== 'complete';
    }
    let attempt = null;
    try {
      attempt = video.play();
    } catch (e) {
      /* the still stays */
    }
    if (attempt && typeof attempt.then === 'function') {
      attempt.catch((error) => {
        if (!error || error.name !== 'NotAllowedError') return;
        video._afVsAskedAt = 0;
        video._afVsRefused = error.name;
        onPreviewRefused();
      });
    }
  }

  function stopPreview(video) {
    if (video && !video.paused) video.pause();
  }

  // A preview whose turn is over goes back to its first frame, so its next turn
  // replays what is already downloaded.
  function restPreview(video) {
    stopPreview(video);
    try {
      if (video.currentTime) video.currentTime = 0;
    } catch (e) {
      /* not seekable yet */
    }
  }

  function setCurrentPreview(group, video) {
    if (group.current && group.current !== video) restPreview(group.current);
    group.current = video;
    clearTimeout(group.timer);
    group.timer = 0;
    startPreview(video);
  }

  function nextPreviewTurn(group) {
    group.timer = 0;
    const inView = inViewOf(group);
    if (inView.length > 1) setCurrentPreview(group, inView[(inView.indexOf(group.current) + 1) % inView.length]);
    schedulePreviews();
  }

  // An "all" row: every card in view plays. A card that has not been on screen
  // yet is started alone and the ones after it wait for it, so the files come
  // down one at a time; a card that has played before resumes at once.
  function playPreviewAll(group, inView) {
    const wanted = inView.slice(0, maxPreviewCards(group));
    group.videos.forEach((video) => {
      if (wanted.indexOf(video) === -1) stopPreview(video);
    });
    let waitingFor = null;
    for (let i = 0; i < wanted.length; i += 1) {
      const video = wanted[i];
      if (video.paused || !video.getAttribute('src')) startPreview(video);
      if (previewBurst) continue;
      // The next card waits for this one's first frame, and while this one is
      // out of data (its 'playing' event comes back here).
      if ((!video.classList.contains('is-ready') && !video._afVsChainPassed) || starving(video)) {
        waitingFor = video;
        break;
      }
    }
    if (waitingFor === group.waitingFor) return;
    clearTimeout(group.timer);
    group.timer = 0;
    group.waitingFor = waitingFor;
    if (!waitingFor) return;
    // A card that never starts must not hold up the rest for ever.
    group.timer = setTimeout(() => {
      group.timer = 0;
      waitingFor._afVsChainPassed = true;
      // Asked for and still not on screen (or out of data all this time): the
      // connection is slow.
      const shown = waitingFor.classList.contains('is-ready');
      if (shown ? starving(waitingFor) : waitingFor._afVsAskedAt && !waitingFor._afVsRefused) {
        markSlow(shown ? 'stalled' : `no first frame in ${PREVIEW_CHAIN_MS / 1000}s`);
      }
      if (group.waitingFor === waitingFor) group.waitingFor = null;
      schedulePreviews();
    }, PREVIEW_CHAIN_MS);
  }

  function playPreviewGroup(group) {
    const inView = inViewOf(group);
    if (!inView.length) {
      stopPreviewGroup(group);
      return;
    }
    if (group.mode === 'all') {
      playPreviewAll(group, inView);
      return;
    }
    const pointed = group.hover && inView.indexOf(group.hover) !== -1 ? group.hover : null;
    const keep = group.current && inView.indexOf(group.current) !== -1 ? group.current : inView[0];
    const wanted = pointed || keep;
    if (group.current !== wanted) setCurrentPreview(group, wanted);
    else if (wanted.paused) startPreview(wanted);
    // In a "turns" row the tiles in view take turns, except while one is pointed at.
    if (group.mode === 'turns' && inView.length > 1 && !pointed) {
      if (!group.timer) group.timer = setTimeout(() => nextPreviewTurn(group), PREVIEW_TURN_MS);
    } else {
      clearTimeout(group.timer);
      group.timer = 0;
    }
  }

  function stopPreviewGroup(group) {
    clearTimeout(group.timer);
    group.timer = 0;
    if (group.current) stopPreview(group.current);
    if (group.mode === 'all') {
      group.waitingFor = null;
      group.videos.forEach(stopPreview);
    }
  }

  function runPreviews() {
    previewFrame = 0;
    // Still being read: DOMContentLoaded comes back here (see the boot code).
    if (doc.readyState === 'loading') return;
    clearTimeout(previewDwellTimer);
    previewDwellTimer = 0;
    previewOrder = previewOrder.filter((group) => previewGroups.get(group.row) === group);
    const live = [];
    if (!previewsHeld() && previewsAllowed()) {
      const now = performance.now();
      let wait = 0;
      previewOrder.forEach((group) => {
        if (!inViewOf(group).length) return;
        const left = PREVIEW_DWELL_MS - (now - group.seenAt);
        if (left > 0) wait = wait ? Math.min(wait, left) : left;
        else live.push(group);
      });
      // A row still inside its wait gets looked at again when the wait is over.
      if (wait) previewDwellTimer = setTimeout(schedulePreviews, wait + 30);
    }
    const chosen = live.slice(0, maxPreviewRows());
    previewGroups.forEach((group) => {
      if (chosen.indexOf(group) !== -1) playPreviewGroup(group);
      else stopPreviewGroup(group);
    });
  }

  function schedulePreviews() {
    if (!previewFrame) previewFrame = requestAnimationFrame(runPreviews);
  }

  function pauseAllPreviews() {
    previewGroups.forEach(stopPreviewGroup);
  }

  function resumePreviews() {
    schedulePreviews();
  }

  /* Pause / play button, one per row (WCAG 2.2.2: moving content can be paused). */

  function updateMotionButtons() {
    const possible = previewsPossible();
    previewGroups.forEach((group) => {
      const button = group.button;
      if (!button) return;
      button.hidden = !possible || previewsRefused;
      button.classList.toggle('is-off', previewsOff);
      button.setAttribute('aria-label', previewsOff ? TEXT.playPreviews : TEXT.pausePreviews);
    });
  }

  function setPreviewsOff(off) {
    previewsOff = !!off;
    updateMotionButtons();
    if (previewsOff) pauseAllPreviews();
    else schedulePreviews();
  }

  function ensureMotionButton(group) {
    if (group.button || group.row === doc.body) return;
    const host = group.row.querySelector('.af-vs-scroller') || group.row;
    const button = create('button', group.bubbles ? 'af-vs-motion af-vs-motion--small' : 'af-vs-motion', { type: 'button' });
    button.innerHTML = `<span class="af-vs-motion__pause">${ICONS.pause}</span><span class="af-vs-motion__play">${ICONS.resume}</span>`;
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      setPreviewsOff(!previewsOff);
    });
    // First in the row, so it is reached before the moving tiles it controls.
    host.insertBefore(button, host.firstChild);
    group.button = button;
    updateMotionButtons();
  }

  /* ------------------------------------------------------------------ */
  /* Cart (mirrors assets/product-form.js + cart-drawer.js)              */
  /* ------------------------------------------------------------------ */

  function shopRoot() {
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return typeof root === 'string' && root ? root : '/';
  }

  const cartAddUrl = () => (window.routes && window.routes.cart_add_url) || `${shopRoot()}cart/add`;
  const cartUrl = () => (window.routes && window.routes.cart_url) || `${shopRoot()}cart`;

  function safePublish(eventKey, data) {
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS[eventKey]) {
        publish(PUB_SUB_EVENTS[eventKey], data);
      }
    } catch (e) {
      /* subscribers failing must not break add to cart */
    }
  }

  function postAddToCart(variantId, button) {
    const cart = doc.querySelector('cart-drawer') || doc.querySelector('cart-notification');
    const canRender = !!(
      cart &&
      typeof cart.getSectionsToRender === 'function' &&
      typeof cart.renderContents === 'function'
    );
    const body = new FormData();
    body.append('id', variantId);
    body.append('quantity', 1);
    if (canRender) {
      body.append(
        'sections',
        cart.getSectionsToRender().map((section) => section.id)
      );
      body.append('sections_url', window.location.pathname);
      if (typeof cart.setActiveElement === 'function') cart.setActiveElement(button);
    }

    let config;
    if (typeof fetchConfig === 'function') {
      config = fetchConfig('javascript');
      config.headers = config.headers || {};
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
    } else {
      config = {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      };
    }
    config.body = body;

    return fetch(cartAddUrl(), config)
      .then((response) => response.json())
      .then((response) => ({ response, cart: canRender ? cart : null }));
  }

  /* ------------------------------------------------------------------ */
  /* Product card                                                        */
  /* ------------------------------------------------------------------ */

  function findVariant(card, id) {
    if (!id) return null;
    return card._afVsVariants.find((variant) => String(variant.id) === String(id)) || null;
  }

  function showCardError(card, message) {
    card._afVsError.textContent = decodeEntities(message || TEXT.error);
    card._afVsError.hidden = false;
  }

  function hideCardError(card) {
    card._afVsError.hidden = true;
    card._afVsError.textContent = '';
  }

  /* Option pills: options with a single value (e.g. one colour) are fixed and
     hidden; every other option (up to two, e.g. Color + Size) gets a pill row.
     Rows cascade: a row's pills only offer values that are purchasable with the
     choices made in the rows above it. The rows sit in the card's panel, which
     stays folded until the card's button is pressed (CARD_COLLAPSED). */

  const cardFolded = (card) => CARD_COLLAPSED && !!card._afVsPanel && !card._afVsOpen;

  // Open or fold a card's choices. The player pauses the slide while they are open.
  function setCardOpen(card, on, moveFocus) {
    if (!CARD_COLLAPSED || !card._afVsPanel) return;
    const next = !!on;
    if (card._afVsOpen === next) return;
    card._afVsOpen = next;
    if (!next) card._afVsPending = false;
    card.classList.toggle('is-open', next);
    card._afVsPanel.hidden = !next;
    card._afVsAtc.setAttribute('aria-expanded', String(next));
    refreshCard(card);
    player.cardToggled(card, next);
    if (!moveFocus) return;
    const target = next
      ? card._afVsPanel.querySelector('.af-vs-card__size[aria-pressed="true"]:not(:disabled)') ||
        card._afVsPanel.querySelector('.af-vs-card__size:not(:disabled), .af-vs-card__select')
      : card._afVsAtc;
    try {
      if (target) target.focus({ preventScroll: true });
    } catch (e) {
      /* ignore */
    }
  }

  function matchesFixed(card, variant) {
    const fixed = card._afVsFixed;
    return Object.keys(fixed).every((idx) => variant.options[idx] === fixed[idx]);
  }

  function valueAvailable(card, rowPos, value) {
    const rows = card._afVsRows;
    const row = rows[rowPos];
    return card._afVsVariants.some((variant) => {
      if (!variant.available || !Array.isArray(variant.options)) return false;
      if (variant.options[row.idx] !== value || !matchesFixed(card, variant)) return false;
      for (let k = 0; k < rowPos; k += 1) {
        const chosen = card._afVsChosen[rows[k].idx];
        if (chosen !== undefined && variant.options[rows[k].idx] !== chosen) return false;
      }
      return true;
    });
  }

  function resolveVariant(card) {
    const rows = card._afVsRows;
    if (!rows.every((row) => card._afVsChosen[row.idx] !== undefined)) return null;
    const match = card._afVsVariants.find(
      (variant) =>
        Array.isArray(variant.options) &&
        matchesFixed(card, variant) &&
        rows.every((row) => variant.options[row.idx] === card._afVsChosen[row.idx])
    );
    return match ? String(match.id) : null;
  }

  function chooseOption(card, idx, value) {
    const rows = card._afVsRows;
    const rowPos = rows.findIndex((row) => String(row.idx) === String(idx));
    if (rowPos === -1 || !valueAvailable(card, rowPos, value)) return;
    card._afVsChosen[rows[rowPos].idx] = value;
    // Drop later choices that are no longer purchasable with this one.
    for (let k = rowPos + 1; k < rows.length; k += 1) {
      const chosen = card._afVsChosen[rows[k].idx];
      if (chosen !== undefined && !valueAvailable(card, k, chosen)) delete card._afVsChosen[rows[k].idx];
    }
    card._afVsVariantId = resolveVariant(card);
    if (card._afVsAdded) {
      clearTimeout(card._afVsAddedTimer);
      card._afVsAdded = false;
      card._afVsAtc.classList.remove('is-added');
    }
    hideCardError(card);
    refreshCard(card);
  }

  function refreshCard(card) {
    const product = card._afVsProduct;
    const variant = findVariant(card, card._afVsVariantId);

    if (card._afVsSizes) {
      card._afVsRows.forEach((row, rowPos) => {
        card._afVsSizes
          .querySelectorAll(`.af-vs-card__size[data-option-index="${row.idx}"]`)
          .forEach((pill) => {
            const value = pill._afVsValue;
            const purchasable = valueAvailable(card, rowPos, value);
            pill.setAttribute('aria-pressed', String(card._afVsChosen[row.idx] === value));
            pill.disabled = !purchasable;
            pill.classList.toggle('is-sold-out', !purchasable);
            if (purchasable) pill.removeAttribute('aria-label');
            else pill.setAttribute('aria-label', `${pill.textContent}, ${TEXT.soldOut}`);
          });
      });
    }

    const now = decodeEntities((variant && variant.price) || product.price || '');
    const was = decodeEntities(variant ? variant.compare_at_price : product.compare_at_price);
    card._afVsPriceNow.textContent = now;
    card._afVsPriceNow.classList.toggle('is-sale', !!was);
    card._afVsPriceWas.textContent = was;
    card._afVsPriceWas.hidden = !was;

    if (card._afVsBusy || card._afVsAdded) return;
    const atc = card._afVsAtc;
    if (!card._afVsAvailable || (variant && !variant.available)) {
      atc.disabled = true;
      atc.textContent = TEXT.soldOut;
    } else if (cardFolded(card)) {
      // Folded: the button opens the choices.
      atc.disabled = false;
      atc.textContent = card._afVsAllPreorder ? TEXT.preorder : TEXT.add;
    } else if (!variant) {
      atc.disabled = true;
      const pending = (card._afVsRows || []).find((row) => card._afVsChosen[row.idx] === undefined);
      const waiting = pending || (card._afVsRows || [])[0];
      atc.textContent = selectLabel(waiting ? waiting.name : (product.options || [])[0] || '');
    } else {
      atc.disabled = false;
      atc.textContent = variant.preorder ? TEXT.preorder : TEXT.add;
    }
  }

  function selectVariant(card, id) {
    const variant = findVariant(card, id);
    if (!variant || !variant.available) return;
    card._afVsVariantId = String(variant.id);
    if (card._afVsAdded) {
      clearTimeout(card._afVsAddedTimer);
      card._afVsAdded = false;
      card._afVsAtc.classList.remove('is-added');
    }
    hideCardError(card);
    refreshCard(card);
  }

  // "Select size": the option's own name comes from the product.
  function selectLabel(name) {
    return TEXT.selectOption.replace('[option]', decodeEntities(name || '')).trim();
  }

  let cardSeq = 0;

  function buildCard(product) {
    const card = create('div', 'af-vs-card');
    const variants = Array.isArray(product.variants) ? product.variants.filter(Boolean) : [];
    const options = Array.isArray(product.options) ? product.options.filter(Boolean) : [];
    const available = variants.filter((variant) => variant.available);
    const url = product.url || '';
    const title = decodeEntities(product.title || '');

    card._afVsProduct = product;
    card._afVsVariants = variants;
    card._afVsVariantId = null;
    card._afVsAvailable = available.length > 0;
    card._afVsAllPreorder = available.length > 0 && available.every((variant) => variant.preorder);
    card._afVsBusy = false;
    card._afVsAdded = false;
    card._afVsPanel = null;
    card._afVsOpen = false;
    card._afVsPending = false;

    // The bar: photo, name and price (one link to the product page) and the button.
    const row = create('div', 'af-vs-card__row');
    const productEl = url ? create('a', 'af-vs-card__product', { href: url }) : create('div', 'af-vs-card__product');
    if (product.image) {
      const thumb = create('span', 'af-vs-card__thumb', { 'aria-hidden': 'true' });
      thumb.appendChild(
        create('img', '', { src: product.image, alt: '', width: 36, height: 48, loading: 'lazy', decoding: 'async' })
      );
      productEl.appendChild(thumb);
    }

    const info = create('span', 'af-vs-card__info');
    const titleEl = create('span', 'af-vs-card__title');
    if (url) {
      const hint = create('span', 'af-vs-sr');
      hint.textContent = `${TEXT.view}: `;
      titleEl.appendChild(hint);
    }
    const titleText = create('span', 'af-vs-card__title-text');
    titleText.textContent = title;
    titleEl.appendChild(titleText);
    const price = create('span', 'af-vs-card__price');
    const priceNow = create('span', 'af-vs-card__price-now');
    const priceWas = create('s', 'af-vs-card__price-was');
    price.append(priceNow, priceWas);
    info.append(titleEl, price);
    card._afVsPriceNow = priceNow;
    card._afVsPriceWas = priceWas;
    productEl.appendChild(info);
    row.appendChild(productEl);

    const atc = create('button', 'af-vs-card__atc', { type: 'button' });
    if (doc.querySelector('cart-drawer')) atc.setAttribute('aria-haspopup', 'dialog');
    row.appendChild(atc);
    card._afVsAtc = atc;
    card.appendChild(row);

    const defaultOnly =
      options.length === 0 || (variants.length === 1 && variants[0].title === 'Default Title');

    // Options with one value (e.g. a single colour) are fixed; the rest vary.
    const optionRows = defaultOnly
      ? []
      : options.map((name, idx) => {
          const values = [];
          variants.forEach((variant) => {
            const value = Array.isArray(variant.options) ? variant.options[idx] : undefined;
            if (value !== undefined && value !== null && values.indexOf(value) === -1) values.push(value);
          });
          return { name, idx, values };
        });
    const varying = optionRows.filter((row) => row.values.length > 1);
    card._afVsRows = varying;
    card._afVsFixed = {};
    card._afVsChosen = {};
    optionRows.forEach((row) => {
      if (row.values.length === 1) card._afVsFixed[row.idx] = row.values[0];
    });

    if (!defaultOnly && varying.length === 0) {
      if (available.length) card._afVsVariantId = String(available[0].id);
    } else if (!defaultOnly && varying.length <= 2) {
      const wrap = create('div', 'af-vs-card__options');
      varying.forEach((row) => {
        const name = decodeEntities(row.name);
        const group = create('div', 'af-vs-card__sizes', { role: 'group', 'aria-label': selectLabel(name) });
        row.values.forEach((value) => {
          const pill = create('button', 'af-vs-card__size', {
            type: 'button',
            'aria-pressed': 'false',
            'data-option-index': row.idx,
          });
          pill._afVsValue = value;
          pill.textContent = decodeEntities(value);
          group.appendChild(pill);
        });
        wrap.appendChild(group);
      });
      card.appendChild(wrap);
      card._afVsSizes = wrap;
      card._afVsPanel = wrap;
      // Preselect a row when only one value is purchasable; with two rows, also
      // preselect the first (e.g. colour) from the first available variant, like
      // the product page, so one tap on a size is enough.
      varying.forEach((row, rowPos) => {
        const buyable = row.values.filter((value) => valueAvailable(card, rowPos, value));
        if (buyable.length === 1) {
          card._afVsChosen[row.idx] = buyable[0];
        } else if (rowPos === 0 && varying.length === 2 && available.length) {
          const first = available.find((variant) => matchesFixed(card, variant));
          if (first) card._afVsChosen[row.idx] = first.options[row.idx];
        }
      });
      card._afVsVariantId = resolveVariant(card);
    } else if (!defaultOnly) {
      const wrap = create('div', 'af-vs-card__select-wrap');
      const select = create('select', 'af-vs-card__select', { 'aria-label': options.join(' / ') });
      variants.forEach((variant) => {
        const option = create('option', '', { value: variant.id });
        option.textContent = decodeEntities(variant.title || '') + (variant.available ? '' : ` – ${TEXT.soldOut}`);
        if (!variant.available) option.disabled = true;
        select.appendChild(option);
      });
      if (available.length) {
        select.value = String(available[0].id);
        card._afVsVariantId = String(available[0].id);
      }
      wrap.appendChild(select);
      card.appendChild(wrap);
      card._afVsSelect = select;
      card._afVsPanel = wrap;
    } else if (available.length) {
      card._afVsVariantId = String(available[0].id);
    }

    if (card._afVsPanel) {
      cardSeq += 1;
      card._afVsPanel.id = `af-vs-card-choices-${cardSeq}`;
      atc.setAttribute('aria-controls', card._afVsPanel.id);
      if (CARD_COLLAPSED) {
        card._afVsPanel.hidden = true;
        atc.setAttribute('aria-expanded', 'false');
      } else {
        card._afVsOpen = true;
        card.classList.add('is-open');
      }
    }

    const error = create('p', 'af-vs-card__error', { role: 'alert' });
    error.hidden = true;
    card.appendChild(error);
    card._afVsError = error;

    refreshCard(card);
    return card;
  }

  /* ------------------------------------------------------------------ */
  /* Instagram posts ("As seen on")                                      */
  /* ------------------------------------------------------------------ */

  // Who posted it, linked to the post on Instagram (opens in a new tab).
  function buildCredit(post) {
    // Her own photo or video (not an Instagram post): the name as she typed it, with no
    // Instagram mark and no link; nothing at all when she gave no name.
    if (post.own) {
      const typed = decodeEntities(post.user || '').trim();
      if (!typed) return null;
      const plain = create('span', 'af-vs-credit af-vs-credit--plain');
      const typedName = create('span', 'af-vs-credit__name');
      typedName.textContent = typed;
      plain.appendChild(typedName);
      return plain;
    }
    const name = decodeEntities(post.user || '').replace(/^@/, '');
    const url = typeof post.url === 'string' && /^https:\/\//i.test(post.url) ? post.url : '';
    if (!name && !url) return null;
    const label = name ? `@${name}` : TEXT.instagram;
    const node = url
      ? create('a', 'af-vs-credit', {
          href: url,
          target: '_blank',
          rel: 'noopener',
          'aria-label': `${label}, ${TEXT.viewPost}`,
        })
      : create('span', 'af-vs-credit');
    node.innerHTML = ICONS.instagram;
    const text = create('span', 'af-vs-credit__name');
    text.textContent = label;
    node.appendChild(text);
    return node;
  }

  // Shopify image URLs carry "&width=N": offer the browser smaller renditions.
  function imageSrcset(src, intrinsic) {
    if (!/[?&]width=\d+/.test(src)) return '';
    const max = Number(intrinsic) || 0;
    const widths = IMAGE_WIDTHS.filter((width) => !max || width <= max);
    if (!widths.length) return '';
    return widths.map((width) => `${src.replace(/([?&]width=)\d+/, `$1${width}`)} ${width}w`).join(', ');
  }

  // Navigating away while the player's history entry is on top: replace that
  // entry instead of stacking a page on it, so Back lands on the page itself.
  function leavePage(url) {
    try {
      if (history.state && history.state.afVs) {
        window.location.replace(url);
        return;
      }
    } catch (e) {
      /* fall through to a normal navigation */
    }
    window.location.href = url;
  }

  function addToCart(card) {
    const button = card._afVsAtc;
    if (card._afVsBusy || button.disabled) return;
    const variantId = card._afVsVariantId;
    const variant = findVariant(card, variantId);
    if (!variant || !variant.available) {
      refreshCard(card);
      return;
    }

    card._afVsBusy = true;
    clearTimeout(card._afVsAddedTimer);
    card._afVsAdded = false;
    button.classList.remove('is-added');
    hideCardError(card);
    button.setAttribute('aria-disabled', 'true');
    button.classList.add('is-loading');
    button.textContent = TEXT.adding;

    postAddToCart(variantId, button)
      .then(({ response, cart }) => {
        if (!response || response.status) {
          safePublish('cartError', {
            source: 'af-video-shop',
            productVariantId: variantId,
            errors: response && (response.errors || response.description),
            message: response && response.message,
          });
          showCardError(card, (response && (response.description || response.message)) || TEXT.error);
          return 'error';
        }
        if (!cart) {
          leavePage(cartUrl());
          return 'redirect';
        }
        safePublish('cartUpdate', { source: 'af-video-shop', productVariantId: variantId, cartData: response });
        cart.classList.remove('is-empty');
        try {
          cart.renderContents(response);
        } catch (e) {
          leavePage(cartUrl());
          return 'redirect';
        }
        return 'added';
      })
      .catch(() => {
        showCardError(card, TEXT.error);
        return 'error';
      })
      .then((outcome) => {
        card._afVsBusy = false;
        button.classList.remove('is-loading');
        button.removeAttribute('aria-disabled');
        if (outcome === 'redirect') return;
        if (outcome === 'added') {
          card._afVsAdded = true;
          button.classList.add('is-added');
          button.textContent = TEXT.added;
          setCardOpen(card, false);
          card._afVsAddedTimer = setTimeout(() => {
            card._afVsAdded = false;
            button.classList.remove('is-added');
            refreshCard(card);
          }, ADDED_MS);
          return;
        }
        refreshCard(card);
      });
  }

  /* ------------------------------------------------------------------ */
  /* Full-screen player (singleton)                                      */
  /* ------------------------------------------------------------------ */

  const player = (() => {
    let el = null;
    let track = null;
    let closeBtn = null;
    let muteBtn = null;
    let pauseBtn = null;
    let prevBtn = null;
    let nextBtn = null;
    let countEl = null;

    // One slide per bubble. A slide has one part (a video or a photo) or, for an
    // Instagram post with several photos or videos, one part per photo or video.
    // Swiping moves between slides; tapping moves between the parts of a slide.
    let slides = [];
    // Payload item index (data-af-vs-open) -> { slide, part }.
    let lookup = [];
    let payload = null;
    let open = false;
    let closing = false;
    let index = 0;
    let navTarget = null;
    let trigger = null;
    let userPaused = false;
    let historyPushed = false;
    let muted = storageGet('sessionStorage', MUTE_KEY) === '1';
    const holds = new Set();
    // The card whose choices are open, if any (one at a time).
    let openCard = null;

    let slideObserver = null;
    let trackResizeObserver = null;
    let cardObserver = null;
    let cartEl = null;
    let cartObserver = null;
    let cartActive = false;
    let closeTimer = 0;
    let hideTimer = 0;
    let scrollTimer = 0;
    let pressTimer = 0;
    let pressed = false;
    let suppressClick = false;
    let loopFrame = 0;
    let lastTick = 0;
    let realignFrame = 0;

    const currentPart = (entry) => (entry ? entry.parts[entry.at] : null);
    // The part its slide is showing / the part on screen right now.
    const isCurrent = (part) => currentPart(part.slide) === part;
    const isLive = (part) => isCurrent(part) && part.slide.index === index;

    /* ---------- DOM ---------- */

    function ensure() {
      if (el) return;
      el = create('div', 'af-vs-player', {
        role: 'dialog',
        'aria-modal': 'true',
        'aria-label': TEXT.dialog,
        tabindex: '-1',
      });
      el.hidden = true;

      closeBtn = create('button', 'af-vs-player__close', { type: 'button', 'aria-label': TEXT.close });
      closeBtn.innerHTML = ICONS.close;
      muteBtn = create('button', 'af-vs-player__mute', { type: 'button', 'aria-label': TEXT.mute });
      muteBtn.innerHTML = `<span class="af-vs-player__icon-sound">${ICONS.sound}</span><span class="af-vs-player__icon-muted">${ICONS.muted}</span>`;
      pauseBtn = create('button', 'af-vs-player__pause', { type: 'button', 'aria-label': TEXT.pause });
      pauseBtn.innerHTML = `<span class="af-vs-player__icon-pause">${ICONS.pause}</span><span class="af-vs-player__icon-play">${ICONS.resume}</span>`;
      countEl = create('div', 'af-vs-player__count', { 'aria-live': 'polite' });
      prevBtn = create('button', 'af-vs-player__prev', { type: 'button', 'aria-label': TEXT.prev });
      prevBtn.innerHTML = ICONS.prev;
      nextBtn = create('button', 'af-vs-player__next', { type: 'button', 'aria-label': TEXT.next });
      nextBtn.innerHTML = ICONS.next;
      track = create('div', 'af-vs-player__track');

      el.append(closeBtn, muteBtn, pauseBtn, countEl, prevBtn, track, nextBtn);
      doc.body.appendChild(el);

      el.addEventListener('click', onClick);
      el.addEventListener('change', onChange);

      track.addEventListener('scroll', onTrackScroll, { passive: true });
      if ('onscrollend' in window) track.addEventListener('scrollend', syncFromScroll);
      track.addEventListener('pointerdown', onPressStart);
      track.addEventListener('pointerup', onPressEnd);
      track.addEventListener('pointercancel', onPressEnd);
      track.addEventListener('pointerleave', onPressEnd);
      track.addEventListener('contextmenu', (event) => {
        // A long press pauses; it must not also open the browser's menu.
        if (event.target instanceof Element && event.target.closest('.af-vs-slide__media')) event.preventDefault();
      });

      if (typeof IntersectionObserver === 'function') {
        slideObserver = new IntersectionObserver(onSlideIntersect, { root: track, threshold: ACTIVE_RATIO });
      }
      if (typeof ResizeObserver === 'function') {
        trackResizeObserver = new ResizeObserver(scheduleRealign);
        trackResizeObserver.observe(track);
        cardObserver = new ResizeObserver((entries) => entries.forEach((entry) => measureCard(entry.target)));
      } else {
        window.addEventListener('resize', scheduleRealign);
      }

      window.addEventListener('popstate', onPopState);
      doc.addEventListener('visibilitychange', onVisibility);
      window.addEventListener('pagehide', () => {
        if (open) setHold('hidden', true);
      });
      window.addEventListener('pageshow', (event) => {
        if (!open) return;
        setHold('hidden', doc.hidden);
        // Restored from bfcache onto the page's own entry (the player entry was
        // replaced when a card link was followed): re-arm Back-to-close.
        if (event.persisted && !(history.state && history.state.afVs)) pushHistory();
      });

      updateMuteUi();
    }

    function buildPart(entry, item) {
      const isImage = item.type === 'image';
      const wrap = create('div', isImage ? 'af-vs-part af-vs-part--image' : 'af-vs-part');
      const part = {
        el: wrap,
        slide: entry,
        item,
        video: null,
        image: null,
        posterSet: false,
        primed: false,
        hlsFailed: false,
        // Photos: loaded yet, time shown so far, and whether that time is up.
        ready: !isImage,
        elapsed: 0,
        ended: false,
      };
      const slide = entry.el;

      if (isImage) {
        // Tall photos fill the screen like a video; the rest are shown whole.
        const aspect = Number(item.aspect) || 0;
        if (aspect > 0 && aspect <= COVER_ASPECT) wrap.classList.add('af-vs-part--cover');
        const image = create('img', 'af-vs-slide__image', {
          alt: decodeEntities(item.alt || ''),
          decoding: 'async',
          draggable: 'false',
        });
        image.addEventListener('load', () => {
          part.ready = true;
          if (isCurrent(part)) slide.classList.remove('is-loading', 'is-error');
        });
        image.addEventListener('error', () => {
          // Keep the timer running so a broken photo does not stall the player.
          part.ready = true;
          if (!isCurrent(part)) return;
          slide.classList.remove('is-loading');
          slide.classList.add('is-error');
        });
        wrap.appendChild(image);
        part.image = image;
        return part;
      }

      const video = create('video', 'af-vs-slide__video', {
        playsinline: true,
        'webkit-playsinline': true,
        preload: 'none',
        disablepictureinpicture: true,
        disableremoteplayback: true,
      });
      video.playsInline = true;
      video.muted = muted;
      video.addEventListener('ended', () => onEnded(part));
      video.addEventListener('waiting', () => {
        if (isLive(part)) slide.classList.add('is-loading');
      });
      video.addEventListener('playing', () => {
        if (isCurrent(part)) slide.classList.remove('is-loading', 'is-error');
      });
      video.addEventListener('canplay', () => {
        if (isCurrent(part)) slide.classList.remove('is-loading');
      });
      video.addEventListener('error', () => {
        const current = video.getAttribute('src');
        if (!current) return;
        if (!part.hlsFailed && item.hls && current === item.hls && (item.hd || item.sd)) {
          // The HLS stream failed here: fall back to the MP4.
          part.hlsFailed = true;
          const live = isLive(part);
          load(part, live ? 'auto' : 'metadata');
          if (live && open) refreshPlayback();
          return;
        }
        if (!isCurrent(part)) return;
        slide.classList.remove('is-loading');
        slide.classList.add('is-error');
      });
      wrap.appendChild(video);
      part.video = video;
      return part;
    }

    function buildSlides(nextPayload) {
      teardownSlides();
      payload = nextPayload;
      const items = payload.items;
      const products = payload.products || {};
      const posts = payload.posts || {};

      // Consecutive items of one Instagram post (a carousel) become one slide
      // with several parts.
      const groups = [];
      lookup = [];
      for (let i = 0; i < items.length; ) {
        const post = items[i] && items[i].post;
        let end = i + 1;
        if (post) while (end < items.length && items[end] && items[end].post === post) end += 1;
        for (let k = i; k < end; k += 1) lookup[k] = { slide: groups.length, part: k - i };
        groups.push(items.slice(i, end).map((item) => item || {}));
        i = end;
      }
      const total = groups.length;
      const fragment = doc.createDocumentFragment();

      slides = groups.map((groupItems, i) => {
        const first = groupItems[0];
        const product = (first.product && products[first.product]) || null;
        const post = (first.post && posts[first.post]) || null;
        const slide = create('div', 'af-vs-slide', {
          role: 'group',
          'aria-roledescription': 'slide',
          'aria-label': `${TEXT.slideOf.replace('[current]', i + 1).replace('[total]', total)}${
            product && product.title ? `: ${decodeEntities(product.title)}` : ''
          }`,
          'data-index': i,
        });
        const entry = { el: slide, index: i, parts: [], at: 0, card: null, bars: [], segments: [], sized: false };

        const media = create('div', 'af-vs-slide__media');
        groupItems.forEach((item) => {
          const part = buildPart(entry, item);
          entry.parts.push(part);
          media.appendChild(part.el);
        });
        entry.parts[0].el.classList.add('is-current');

        const state = create('span', 'af-vs-slide__state', { 'aria-hidden': 'true' });
        state.innerHTML = ICONS.play;
        const spinner = create('span', 'af-vs-slide__spinner', { 'aria-hidden': 'true' });
        media.append(state, spinner);

        // One bar for a single video or photo; one segment per part for a post
        // with several, like a story.
        const progress = create('div', 'af-vs-slide__progress', { 'aria-hidden': 'true' });
        if (entry.parts.length > 1) {
          progress.classList.add('af-vs-slide__progress--segments');
          entry.parts.forEach(() => {
            const segment = create('div', 'af-vs-slide__seg');
            const fill = create('span');
            segment.appendChild(fill);
            progress.appendChild(segment);
            entry.segments.push(segment);
            entry.bars.push(fill);
          });
        } else {
          const bar = create('span');
          progress.appendChild(bar);
          entry.bars.push(bar);
        }

        slide.append(media, progress);
        entry.card = product ? buildCard(product) : null;
        if (entry.card) slide.appendChild(entry.card);
        const credit = post ? buildCredit(post) : null;
        if (credit) slide.appendChild(credit);
        fragment.appendChild(slide);

        // Photos stay clear of the card and the credit sits just above it.
        entry.sized = !!entry.card && (!!credit || entry.parts.some((part) => part.image));
        if (entry.sized && cardObserver) cardObserver.observe(entry.card);
        setInert(slide, true);
        return entry;
      });

      track.appendChild(fragment);
      if (slideObserver) slides.forEach((entry) => slideObserver.observe(entry.el));
    }

    function teardownSlides() {
      slides.forEach((entry) => {
        if (slideObserver) slideObserver.unobserve(entry.el);
        entry.parts.forEach(unload);
      });
      if (cardObserver) cardObserver.disconnect();
      slides = [];
      lookup = [];
      payload = null;
      if (track) track.textContent = '';
    }

    // Shopify's theme-preview bar (there only while an unpublished theme is
    // previewed) lies over the bottom of the page: the player stops above it.
    function applyPreviewBar() {
      const bar = doc.getElementById('PBarNextFrameWrapper') || doc.getElementById('preview-bar-iframe');
      const box = bar ? bar.getBoundingClientRect() : null;
      const covers = !!box && box.height > 0 && box.top > 0 && box.bottom >= window.innerHeight - 1;
      el.style.setProperty('--af-vs-bar', `${covers ? Math.round(window.innerHeight - box.top) : 0}px`);
    }

    // Each slide knows its product card's height (CSS --af-vs-card-h).
    function measureCard(card) {
      const slide = card.parentNode;
      if (!slide || !slide.style) return;
      // The photo and the credit are placed against the folded bar: a card
      // that opens grows over them instead of moving them.
      if (CARD_COLLAPSED && card._afVsOpen) return;
      const height = Math.round(card.offsetHeight);
      // 0 means the player is hidden: keep the last real value for the next open.
      if (height > 0) slide.style.setProperty('--af-vs-card-h', `${height}px`);
    }

    function setInert(node, on) {
      if (hasInert) node.inert = on;
      if (on) node.setAttribute('aria-hidden', 'true');
      else node.removeAttribute('aria-hidden');
    }

    /* ---------- sources ---------- */

    function sourceOf(part) {
      if (canHls && part.item.hls && !part.hlsFailed) return part.item.hls;
      return part.item.hd || part.item.sd || '';
    }

    function load(part, preload) {
      if (part.image) {
        loadImage(part);
        return;
      }
      const url = sourceOf(part);
      if (!url) return;
      const video = part.video;
      if (video.preload !== preload) video.preload = preload;
      if (video.getAttribute('src') !== url) video.src = url;
    }

    function loadImage(part) {
      const image = part.image;
      if (image.getAttribute('src')) return;
      const src = part.item.src || '';
      if (!src) {
        part.ready = true;
        return;
      }
      const srcset = imageSrcset(src, part.item.w);
      if (srcset) {
        image.setAttribute('sizes', IMAGE_SIZES);
        image.setAttribute('srcset', srcset);
      }
      image.setAttribute('src', src);
      if (image.complete && image.naturalWidth > 0) part.ready = true;
    }

    function unload(part) {
      const video = part.video;
      // Photos stay loaded: there is no stream to release.
      if (!video) return;
      if (!video.paused) video.pause();
      if (video.hasAttribute('src')) {
        video.removeAttribute('src');
        try {
          video.load();
        } catch (e) {
          /* ignore */
        }
      }
    }

    function ensurePoster(part) {
      if (!part.video || part.posterSet || !part.item.poster) return;
      part.video.poster = part.item.poster;
      part.posterSet = true;
    }

    // Back to the start of one video or photo.
    function resetPart(part) {
      const video = part.video;
      if (video) {
        if (!video.paused) video.pause();
        try {
          if (video.currentTime) video.currentTime = 0;
        } catch (e) {
          /* not seekable yet */
        }
      }
      part.elapsed = 0;
      part.ended = false;
    }

    // Show one part of a slide (DOM only): earlier segments full, this one empty.
    function showPart(entry, at) {
      entry.at = clamp(at, 0, entry.parts.length - 1);
      entry.parts.forEach((part, i) => part.el.classList.toggle('is-current', i === entry.at));
      entry.bars.forEach((bar, i) => {
        const done = i < entry.at;
        if (entry.segments[i]) entry.segments[i].classList.toggle('is-done', done);
        bar.style.transform = done ? '' : 'scaleX(0)';
      });
      entry.el.classList.remove('is-loading', 'is-error');
    }

    // The active slide: stream its current part, warm up the next one, release the rest.
    function loadAround(entry) {
      entry.parts.forEach((part, i) => {
        if (part.image) {
          if (Math.abs(i - entry.at) <= 1) loadImage(part);
        } else if (i === entry.at) {
          ensurePoster(part);
          load(part, 'auto');
        } else if (i === entry.at + 1) {
          ensurePoster(part);
          load(part, 'metadata');
        } else {
          unload(part);
        }
      });
    }

    /* ---------- playback ---------- */

    function setHold(name, on) {
      const had = holds.has(name);
      if (on) holds.add(name);
      else holds.delete(name);
      if (open && had !== on) refreshPlayback();
    }

    // A card that shows its choices pauses the slide until it folds again.
    function cardToggled(card, on) {
      if (on) {
        const other = openCard;
        openCard = card;
        if (other && other !== card) setCardOpen(other, false);
      } else if (openCard === card) {
        openCard = null;
      }
      setHold('card', !!openCard);
    }

    // Fold the open card as part of a move that restarts playback itself.
    function foldCardQuietly() {
      if (!openCard) return;
      holds.delete('card');
      setCardOpen(openCard, false);
    }

    function updatePauseUi() {
      if (!el) return;
      const paused = open && userPaused;
      el.classList.toggle('is-user-paused', paused);
      pauseBtn.setAttribute('aria-label', paused ? TEXT.play : TEXT.pause);
    }

    function refreshPlayback() {
      updatePauseUi();
      const active = slides[index];
      const part = currentPart(active);
      if (!part) return;
      const shouldPlay = open && !userPaused && holds.size === 0;
      active.el.classList.toggle('is-paused', open && userPaused);
      slides.forEach((entry) => {
        entry.parts.forEach((other) => {
          if (other !== part && other.video && !other.video.paused) other.video.pause();
        });
      });
      if (!part.video) {
        // Photo: tick() runs its timer whenever playback is allowed.
        active.el.classList.toggle('is-loading', shouldPlay && !part.ready);
        return;
      }
      if (shouldPlay) play(part);
      else if (!part.video.paused) part.video.pause();
    }

    function play(part) {
      const video = part.video;
      if (!video || !sourceOf(part)) return;
      if (!video.getAttribute('src')) load(part, 'auto');
      if (video.paused && video.readyState < 3) part.slide.el.classList.add('is-loading');
      video.muted = muted;
      let attempt;
      try {
        attempt = video.play();
      } catch (e) {
        attempt = null;
      }
      if (!attempt || typeof attempt.then !== 'function') return;
      attempt.catch((err) => {
        if (!open || !isLive(part)) return;
        if (err && err.name === 'AbortError') return;
        if (err && err.name === 'NotAllowedError' && !video.muted) {
          muted = true;
          updateMuteUi();
          video.muted = true;
          let retry;
          try {
            retry = video.play();
          } catch (e) {
            retry = null;
          }
          if (retry && typeof retry.then === 'function') {
            retry.catch((retryErr) => {
              if (retryErr && retryErr.name === 'AbortError') return;
              if (open && isLive(part)) showPaused();
            });
          }
          return;
        }
        showPaused();
      });
    }

    function showPaused() {
      userPaused = true;
      const active = slides[index];
      if (active) active.el.classList.add('is-paused');
      updatePauseUi();
    }

    function togglePause() {
      if (!open || !slides[index]) return;
      userPaused = !userPaused;
      refreshPlayback();
    }

    // Called inside a user gesture: lets every <video> later start with sound
    // (iOS Safari grants unmuted playback per element on a gesture).
    // iOS also pauses other media when an element begins playback, so callers
    // must (re)start the active video AFTER priming.
    function prime(skipPart) {
      if (muted) return;
      slides.forEach((entry) => {
        entry.parts.forEach((part) => {
          if (!part.video || part.primed || part === skipPart) return;
          part.primed = true;
          const video = part.video;
          video.muted = false;
          try {
            quiet(video.play());
            video.pause();
          } catch (e) {
            /* ignore */
          }
        });
      });
      if (skipPart) skipPart.primed = true;
    }

    // A video finished or a photo's time is up: next part, then next slide.
    function onEnded(part) {
      if (!open || !isLive(part)) return;
      const entry = part.slide;
      if (entry.at < entry.parts.length - 1) {
        setPart(entry, entry.at + 1);
      } else if (index < slides.length - 1) {
        goTo(index + 1, true);
      } else if (part.video) {
        try {
          part.video.currentTime = 0;
        } catch (e) {
          /* ignore */
        }
        refreshPlayback();
      }
      // A photo at the very end simply stays up.
    }

    function toggleMute() {
      muted = !muted;
      storageSet('sessionStorage', MUTE_KEY, muted ? '1' : '0');
      updateMuteUi();
      const part = currentPart(slides[index]);
      if (!muted) prime(part);
      if (!part || !part.video) return;
      part.video.muted = muted;
      if (!userPaused && holds.size === 0) play(part);
    }

    function updateMuteUi() {
      if (!el) return;
      el.classList.toggle('is-muted', muted);
      muteBtn.setAttribute('aria-label', muted ? TEXT.unmute : TEXT.mute);
    }

    function tick(now) {
      loopFrame = 0;
      if (!open) return;
      const stamp = typeof now === 'number' ? now : performance.now();
      // Capped so time spent in a background tab does not count.
      const delta = lastTick ? Math.min(Math.max(stamp - lastTick, 0), 100) : 0;
      lastTick = stamp;
      const active = slides[index];
      const part = currentPart(active);
      if (part && part.video) {
        const video = part.video;
        const duration = video.duration;
        if (duration && isFinite(duration) && duration > 0) {
          active.bars[active.at].style.transform = `scaleX(${Math.min(1, video.currentTime / duration)})`;
        }
      } else if (part && part.ready && !part.ended && !userPaused && holds.size === 0) {
        // Photos have no clock of their own: show each for IMAGE_MS, then move on.
        part.elapsed += delta;
        const done = Math.min(1, part.elapsed / IMAGE_MS);
        active.bars[active.at].style.transform = `scaleX(${done})`;
        if (done >= 1) {
          part.ended = true;
          onEnded(part);
        }
      }
      loopFrame = requestAnimationFrame(tick);
    }

    /* ---------- navigation ---------- */

    function slideWidth() {
      return track.getBoundingClientRect().width || track.clientWidth || 1;
    }

    function offsetOf(i) {
      const entry = slides[i];
      return entry ? entry.el.offsetLeft : 0;
    }

    function setActive(i, force) {
      if (!slides.length) return;
      const target = clamp(i, 0, slides.length - 1);
      if (target === index && !force) return;
      const previous = slides[index];
      const hadFocus = !!(previous && index !== target && previous.el.contains(doc.activeElement));
      if (index !== target) foldCardQuietly();
      if (previous && index !== target) {
        // Leaving a slide: it keeps its place (a post reopens on the photo you
        // were on), but that photo or video starts again from its beginning.
        resetPart(currentPart(previous));
        previous.bars[previous.at].style.transform = 'scaleX(0)';
        previous.el.classList.remove('is-paused', 'is-loading');
      }
      index = target;
      if (navTarget === target) navTarget = null;
      userPaused = false;

      slides.forEach((entry) => {
        const distance = Math.abs(entry.index - target);
        const isActive = distance === 0;
        entry.el.classList.toggle('is-active', isActive);
        setInert(entry.el, !isActive);
        if (isActive) return;
        // Neighbours: only the part they will open on is kept warm.
        const lead = currentPart(entry);
        entry.parts.forEach((part) => {
          if (part.image) {
            if (part === lead && distance <= 2) loadImage(part);
          } else if (part !== lead || distance > 2) {
            unload(part);
          } else {
            ensurePoster(part);
            if (distance === 1) load(part, 'metadata');
            else if (part.video.hasAttribute('src')) part.video.preload = 'metadata';
          }
        });
      });

      const active = slides[target];
      resetPart(currentPart(active));
      showPart(active, active.at);
      loadAround(active);
      updateChrome();
      if (hadFocus) {
        // The old slide is inert now; move focus to the same kind of control on the new one.
        const next =
          active.el.querySelector('.af-vs-card__atc:not([disabled])') || active.el.querySelector('.af-vs-card a[href]');
        try {
          (next || el).focus({ preventScroll: true });
        } catch (e) {
          /* ignore */
        }
      }
      markSeen(active.parts[0].item.id);
      refreshPlayback();
    }

    // Move to another photo or video of the active slide.
    function setPart(entry, at) {
      foldCardQuietly();
      const target = clamp(at, 0, entry.parts.length - 1);
      const previous = currentPart(entry);
      if (previous) resetPart(previous);
      showPart(entry, target);
      resetPart(entry.parts[target]);
      userPaused = false;
      loadAround(entry);
      updateChrome();
      refreshPlayback();
    }

    // Tap on the right: next photo or video of the post, then the next slide.
    // Returns false at the very end.
    function forward() {
      const entry = slides[index];
      if (!entry) return false;
      if (entry.at < entry.parts.length - 1) {
        setPart(entry, entry.at + 1);
        return true;
      }
      if (index < slides.length - 1) {
        goTo(index + 1, false);
        return true;
      }
      return false;
    }

    // Tap on the left: previous photo or video, then the previous slide; at the
    // very start, replay.
    function back() {
      const entry = slides[index];
      if (!entry) return;
      if (entry.at > 0) setPart(entry, entry.at - 1);
      else if (index > 0) goTo(index - 1, false);
      else setPart(entry, 0);
    }

    function updateChrome() {
      const total = slides.length;
      countEl.textContent = total > 1 ? `${index + 1} / ${total}` : '';
      countEl.hidden = total < 2;
      prevBtn.hidden = index <= 0;
      nextBtn.hidden = index >= total - 1;
      // Photos have no sound: the mute button steps aside.
      const part = currentPart(slides[index]);
      el.classList.toggle('is-image', !!(part && part.image));
    }

    function goTo(i, smooth) {
      if (!slides.length) return;
      const target = clamp(i, 0, slides.length - 1);
      const behavior = smooth && !reducedMotion() ? 'smooth' : 'auto';
      navTarget = target;
      try {
        track.scrollTo({ left: offsetOf(target), behavior });
      } catch (e) {
        track.scrollLeft = offsetOf(target);
      }
      if (behavior === 'auto') setActive(target);
    }

    function step(direction) {
      const base = navTarget !== null ? navTarget : index;
      goTo(base + direction, true);
    }

    function onSlideIntersect(entries) {
      if (!open) return;
      let best = null;
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio >= ACTIVE_RATIO) {
          if (!best || entry.intersectionRatio > best.intersectionRatio) best = entry;
        }
      });
      if (best) setActive(Number(best.target.getAttribute('data-index')));
    }

    function syncFromScroll() {
      clearTimeout(scrollTimer);
      if (!open || !slides.length) return;
      navTarget = null;
      setActive(Math.round(track.scrollLeft / slideWidth()));
    }

    function onTrackScroll() {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(syncFromScroll, 140);
    }

    function scheduleRealign() {
      if (!open || realignFrame) return;
      realignFrame = requestAnimationFrame(() => {
        realignFrame = 0;
        if (!open || !slides[index]) return;
        applyPreviewBar();
        const left = offsetOf(index);
        if (Math.abs(track.scrollLeft - left) > 1) track.scrollLeft = left;
      });
    }

    /* ---------- events ---------- */

    // Press and hold on the photo or video pauses it; letting go resumes.
    function onPressStart(event) {
      if (!open || event.button > 0) return;
      const target = event.target;
      if (!(target instanceof Element) || !target.closest('.af-vs-slide__media')) return;
      clearTimeout(pressTimer);
      pressTimer = setTimeout(() => {
        pressed = true;
        setHold('press', true);
      }, PRESS_MS);
    }

    function onPressEnd() {
      clearTimeout(pressTimer);
      if (!pressed) return;
      pressed = false;
      setHold('press', false);
      // The click that ends a long press must not also move on.
      suppressClick = true;
      setTimeout(() => {
        suppressClick = false;
      }, 350);
    }

    function onClick(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;

      if (target.closest('.af-vs-player__close')) {
        requestClose();
        return;
      }
      if (target.closest('.af-vs-player__mute')) {
        toggleMute();
        return;
      }
      if (target.closest('.af-vs-player__pause')) {
        togglePause();
        return;
      }
      if (target.closest('.af-vs-player__prev')) {
        step(-1);
        return;
      }
      if (target.closest('.af-vs-player__next')) {
        step(1);
        return;
      }

      const card = target.closest('.af-vs-card');
      if (card) {
        const pill = target.closest('.af-vs-card__size');
        if (pill) {
          if (pill.disabled) return;
          const optionIndex = pill.getAttribute('data-option-index');
          chooseOption(card, optionIndex, pill._afVsValue);
          // "Add to cart" opened the choices: picking the last one completes it.
          const rows = card._afVsRows || [];
          const lastRow = rows.length > 0 && String(rows[rows.length - 1].idx) === String(optionIndex);
          if (card._afVsPending && lastRow && card._afVsVariantId) {
            card._afVsPending = false;
            addToCart(card);
          }
          return;
        }
        if (target.closest('.af-vs-card__atc')) {
          if (cardFolded(card)) {
            card._afVsPending = true;
            setCardOpen(card, true, true);
          } else {
            addToCart(card);
          }
          return;
        }
        const link = target.closest('a[href]');
        if (
          link &&
          !event.defaultPrevented &&
          event.button === 0 &&
          !event.metaKey &&
          !event.ctrlKey &&
          !event.shiftKey &&
          !event.altKey &&
          link.origin === window.location.origin &&
          (!link.target || link.target === '_self')
        ) {
          event.preventDefault();
          if (productPath(link.pathname) === productPath(window.location.pathname)) {
            // Already on this product page: just close the player.
            requestClose();
          } else {
            leavePage(link.href);
          }
        }
        return;
      }

      const media = target.closest('.af-vs-slide__media');
      if (media) {
        const slide = media.closest('.af-vs-slide');
        if (!slide || Number(slide.getAttribute('data-index')) !== index) return;
        if (suppressClick) {
          suppressClick = false;
          return;
        }
        // A card is showing its choices: a tap outside it only folds it.
        if (openCard) {
          setCardOpen(openCard, false);
          return;
        }
        // Paused (the play mark is showing): a tap anywhere resumes.
        if (userPaused) {
          togglePause();
          return;
        }
        // Like a story: the left edge goes back, everywhere else goes forward.
        const box = media.getBoundingClientRect();
        if (event.clientX - box.left < box.width * BACK_ZONE) back();
        else if (!forward()) requestClose();
        return;
      }

      if (target === el) requestClose();
    }

    function onChange(event) {
      const select = event.target;
      if (!(select instanceof HTMLSelectElement) || !select.classList.contains('af-vs-card__select')) return;
      const card = select.closest('.af-vs-card');
      if (card) selectVariant(card, select.value);
    }

    function focusables() {
      return Array.from(
        el.querySelectorAll('button, a[href], select, [tabindex]:not([tabindex="-1"])')
      ).filter((node) => {
        if (node.disabled || node.hidden || node.getAttribute('tabindex') === '-1') return false;
        if (node.closest('[inert]')) return false;
        const slide = node.closest('.af-vs-slide');
        if (slide && !slide.classList.contains('is-active')) return false;
        if (!node.getClientRects().length) return false;
        return getComputedStyle(node).visibility !== 'hidden';
      });
    }

    function trapTab(event) {
      const list = focusables();
      if (!list.length) {
        event.preventDefault();
        el.focus({ preventScroll: true });
        return;
      }
      const first = list[0];
      const last = list[list.length - 1];
      const current = doc.activeElement;
      if (!el.contains(current) || current === el) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus({ preventScroll: true });
      } else if (event.shiftKey && current === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && current === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    }

    function onKeydown(event) {
      if (!open || holds.has('cart') || event.defaultPrevented) return;
      const key = event.key;
      if (key === 'Escape' || key === 'Esc') {
        event.preventDefault();
        // Escape folds an open card first, then closes the player.
        if (openCard) setCardOpen(openCard, false, true);
        else requestClose();
        return;
      }
      if (key === 'Tab') {
        trapTab(event);
        return;
      }
      const target = event.target;
      const tag = target && target.tagName;
      if (tag === 'SELECT' || tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (key === 'ArrowRight' || key === 'ArrowLeft') {
        event.preventDefault();
        if (key === 'ArrowRight') forward();
        else back();
        return;
      }
      if ((key === ' ' || key === 'Spacebar') && (target === el || target === doc.body || target === html)) {
        event.preventDefault();
        togglePause();
      }
    }

    function onVisibility() {
      if (open) setHold('hidden', doc.hidden);
    }

    function watchCart() {
      const drawer = doc.querySelector('cart-drawer');
      if (drawer === cartEl) {
        cartActive = !!(cartEl && cartEl.classList.contains('active'));
        return;
      }
      if (cartObserver) cartObserver.disconnect();
      cartObserver = null;
      cartEl = drawer;
      cartActive = !!(drawer && drawer.classList.contains('active'));
      if (!drawer || typeof MutationObserver !== 'function') return;
      cartObserver = new MutationObserver(() => {
        const active = cartEl.classList.contains('active');
        if (active === cartActive) return;
        cartActive = active;
        setHold('cart', active);
      });
      cartObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }

    /* ---------- history ---------- */

    function pushHistory() {
      historyPushed = false;
      try {
        const current = history.state && typeof history.state === 'object' ? history.state : {};
        const state = Object.assign({}, current, { afVs: true });
        // facets.js re-renders on popstate when searchParams differ; keep them identical.
        if (state.searchParams === undefined) state.searchParams = window.location.search.slice(1);
        history.pushState(state, '');
        historyPushed = true;
      } catch (e) {
        historyPushed = false;
      }
    }

    function onPopState() {
      if (!open) return;
      historyPushed = false;
      finishClose();
    }

    /* ---------- open / close ---------- */

    // startIndex is a payload item index (what data-af-vs-open carries).
    function locate(startIndex) {
      if (!lookup.length) return { slide: 0, part: 0 };
      return lookup[clamp(Number(startIndex) || 0, 0, lookup.length - 1)] || { slide: 0, part: 0 };
    }

    function openPlayer(nextPayload, startIndex, fromTrigger) {
      if (!nextPayload || !nextPayload.items || !nextPayload.items.length) return;
      ensure();
      clearTimeout(hideTimer);

      if (open) {
        if (nextPayload === payload && !closing) {
          const spot = locate(startIndex);
          if (spot.slide === index) {
            setPart(slides[index], spot.part);
          } else if (slides[spot.slide]) {
            showPart(slides[spot.slide], spot.part);
            goTo(spot.slide, false);
          }
        }
        return;
      }

      if (nextPayload !== payload) buildSlides(nextPayload);
      const spot = locate(startIndex);
      const start = clamp(spot.slide, 0, slides.length - 1);

      trigger = fromTrigger || null;
      open = true;
      closing = false;
      userPaused = false;
      navTarget = null;
      holds.clear();
      if (doc.hidden) holds.add('hidden');
      watchCart();

      el.hidden = false;
      applyPreviewBar();
      html.classList.add('af-vs-lock');
      pauseAllPreviews();
      lastTick = 0;

      // Layout is live now: size the photo slides against their cards, then
      // jump straight to the tapped slide, no animation.
      slides.forEach((entry) => {
        if (entry.sized) measureCard(entry.card);
      });
      showPart(slides[start], spot.part);
      track.scrollLeft = offsetOf(start);
      prime(currentPart(slides[start]));
      index = -1;
      setActive(start, true);

      if (!loopFrame) loopFrame = requestAnimationFrame(tick);
      // Commit the hidden -> shown style first so the fade-in transition runs.
      void el.offsetWidth;
      el.classList.add('is-open');

      doc.addEventListener('keydown', onKeydown);
      try {
        closeBtn.focus({ preventScroll: true });
      } catch (e) {
        closeBtn.focus();
      }
      pushHistory();
    }

    function requestClose() {
      if (!open || closing) return;
      closing = true;
      if (historyPushed && history.state && history.state.afVs) {
        clearTimeout(closeTimer);
        // popstate normally closes us; this is a safety net if it never fires.
        closeTimer = setTimeout(() => {
          historyPushed = false;
          finishClose();
        }, 600);
        history.back();
      } else {
        historyPushed = false;
        finishClose();
      }
    }

    function finishClose() {
      if (!open) return;
      clearTimeout(closeTimer);
      clearTimeout(scrollTimer);
      clearTimeout(pressTimer);
      open = false;
      closing = false;
      pressed = false;
      suppressClick = false;
      navTarget = null;
      if (openCard) setCardOpen(openCard, false);
      holds.clear();
      doc.removeEventListener('keydown', onKeydown);
      if (loopFrame) cancelAnimationFrame(loopFrame);
      loopFrame = 0;
      lastTick = 0;

      // Every slide goes back to its first photo or video for the next open.
      slides.forEach((entry) => {
        entry.parts.forEach((part) => {
          resetPart(part);
          unload(part);
        });
        showPart(entry, 0);
        entry.el.classList.remove('is-paused');
      });

      el.classList.remove('is-open');
      html.classList.remove('af-vs-lock');
      const hide = () => {
        if (!open) el.hidden = true;
      };
      clearTimeout(hideTimer);
      if (reducedMotion()) hide();
      else hideTimer = setTimeout(hide, FADE_MS);

      resumePreviews();

      const returnTo = trigger;
      trigger = null;
      if (returnTo && returnTo.isConnected && typeof returnTo.focus === 'function') {
        try {
          returnTo.focus({ preventScroll: true });
        } catch (e) {
          /* ignore */
        }
      }
    }

    return {
      open: openPlayer,
      close: requestClose,
      isOpen: () => open,
      cardToggled,
    };
  })();

  /* ------------------------------------------------------------------ */
  /* Trigger delegation                                                  */
  /* ------------------------------------------------------------------ */

  doc.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const opener = target.closest('[data-af-vs-open]');
    if (!opener) return;
    const root = opener.closest('[data-af-vs-root]');
    if (!root) return;
    const payload = getPayload(root);
    if (!payload) return;
    event.preventDefault();
    const at = parseInt(opener.getAttribute('data-af-vs-open'), 10);
    player.open(payload, Number.isFinite(at) ? at : 0, opener);
  });

  /* ------------------------------------------------------------------ */
  /* Custom elements                                                     */
  /* ------------------------------------------------------------------ */

  class AfVsWidget extends HTMLElement {
    connectedCallback() {
      if (this._afVsReady || !this.isConnected) return;
      const scroller = this.querySelector('.af-vs-scroller');
      if (!scroller && doc.readyState === 'loading') {
        // Children not parsed yet (element upgraded mid-parse): finish once they are.
        doc.addEventListener('DOMContentLoaded', () => this.connectedCallback(), { once: true });
        return;
      }
      this._afVsReady = true;
      this._afVsScroller = initScroller(scroller, this);
      this._afVsRoving = initRoving(this.querySelector('[data-af-vs-track]'));
      this._afVsVideos = Array.from(this.querySelectorAll('video[data-af-vs-autoplay]'));
      this._afVsVideos.forEach(registerPreview);
      applySeen(this);
      if (this.closest('li.af-vs-reel-item[data-af-vs-reel-rows]')) scheduleReels();
    }

    disconnectedCallback() {
      if (!this._afVsReady) return;
      this._afVsReady = false;
      if (this._afVsScroller) this._afVsScroller.destroy();
      this._afVsScroller = null;
      if (this._afVsRoving) this._afVsRoving.destroy();
      this._afVsRoving = null;
      (this._afVsVideos || []).forEach(unregisterPreview);
      this._afVsVideos = null;
    }
  }

  const define = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };
  define('af-vs-stories', class AfVsStories extends AfVsWidget {});
  define('af-vs-reel', class AfVsReel extends AfVsWidget {});
  define('af-vs-scroller', class AfVsScroller extends AfVsWidget {});

  /* ------------------------------------------------------------------ */
  /* #af-debug: what the previews are doing on this device               */
  /* ------------------------------------------------------------------ */

  // Add #af-debug to the end of any page's address to see, on the page itself,
  // why its previews did or did not start on this device (a screenshot of the
  // box is the bug report). Nothing is built or timed without it.
  let debugPanel = null;
  let debugTimer = 0;

  function debugLines() {
    const ua = navigator.userAgent || '';
    const ios = /iP(hone|ad|od)/.test(ua) ? (ua.match(/OS (\d+(?:_\d+)*)/) || [])[1] || '' : '';
    let app = 'other browser';
    if (/Instagram/.test(ua)) app = 'Instagram in-app browser';
    else if (/FBAN|FBAV/.test(ua)) app = 'Facebook in-app browser';
    else if (/CriOS|Chrome/.test(ua)) app = 'Chrome';
    else if (/FxiOS|Firefox/.test(ua)) app = 'Firefox';
    else if (/Safari/.test(ua)) app = 'Safari';
    const link = navigator.connection;
    const sec = (ms) => `${(ms / 1000).toFixed(1)}s`;
    const lines = [
      `af-video-shop ${BUILD} | ${ios ? `iOS ${ios.replace(/_/g, '.')} ` : ''}${app}`,
      `reduce motion: ${reducedMotion() ? 'ON (previews play anyway)' : 'off'} | save data: ${saveData() ? 'ON, so previews stay off' : 'off'}`,
      `connection: ${link ? `${link.effectiveType || '?'} ${link.downlink || '?'} Mbps` : 'not reported by this browser'} | page: ${doc.readyState} at ${sec(performance.now())}`,
      `refused by the browser: ${previewsRefused ? 'YES, they start at the first tap' : 'no'} | one-per-row rule: ${previewSlow ? `ON (${previewSlowWhy})` : 'off'} | paused by button: ${previewsOff ? 'yes' : 'no'}`,
    ];
    previewGroups.forEach((group) => {
      let name = 'cards';
      if (group.bubbles) name = 'bubbles';
      else if (group.row !== doc.body && group.row.matches('af-vs-reel')) name = 'reel';
      const playing = group.videos.filter((video) => !video.paused && video.classList.contains('is-ready')).length;
      lines.push(`${name}: ${group.videos.length} videos, ${inViewOf(group).length} on screen, ${playing} playing`);
      group.videos.slice(0, 6).forEach((video, i) => {
        let state = 'loading';
        if (video._afVsRefused) state = `REFUSED (${video._afVsRefused})`;
        else if (video.error) state = `error ${video.error.code}`;
        else if (!video.getAttribute('src')) state = video._afVsInView ? 'waiting its turn' : 'not on screen';
        else if (video.paused) state = 'stopped';
        else if (video.classList.contains('is-ready')) state = 'PLAYING';
        const notes = [];
        if (video._afVsEarly) notes.push('started with the page');
        if (video._afVsAskedAt) notes.push(`asked ${sec(video._afVsAskedAt)}`);
        if (video._afVsShownAt) notes.push(`first frame ${sec(video._afVsShownAt)}`);
        if (video._afVsStalls) notes.push(`stalled ${video._afVsStalls}x`);
        lines.push(`  ${i + 1}. ${state}${notes.length ? ` (${notes.join(', ')})` : ''}`);
      });
    });
    if (!previewGroups.size) lines.push('no preview videos on this page');
    return lines;
  }

  function renderDebug() {
    const wanted = /af-debug/.test(location.hash);
    if (!wanted || !doc.body) {
      if (debugPanel) debugPanel.remove();
      debugPanel = null;
      clearInterval(debugTimer);
      debugTimer = 0;
      return;
    }
    if (!debugPanel) {
      debugPanel = create('pre', 'af-vs-debug');
      debugPanel.setAttribute(
        'style',
        'position:fixed;left:8px;right:8px;z-index:2147483000;max-height:46vh;overflow:auto;margin:0;padding:8px 10px;font:11px/1.45 ui-monospace,Menlo,monospace;white-space:pre-wrap;text-align:left;background:rgba(0,0,0,.88);color:#fff;border-radius:6px;-webkit-user-select:text;user-select:text;'
      );
      doc.body.appendChild(debugPanel);
      debugTimer = setInterval(renderDebug, 500);
    }
    // Above Shopify's preview bar, when the page is a theme preview.
    const bar = doc.getElementById('PBarNextFrameWrapper') || doc.getElementById('preview-bar-iframe');
    const barHeight = bar ? Math.round(bar.getBoundingClientRect().height) : 0;
    debugPanel.style.bottom = `${barHeight + 8}px`;
    debugPanel.textContent = debugLines().join('\n');
  }

  /* ------------------------------------------------------------------ */
  /* Global listeners + boot                                             */
  /* ------------------------------------------------------------------ */

  doc.addEventListener('visibilitychange', () => {
    if (doc.hidden) pauseAllPreviews();
    else resumePreviews();
  });

  onMqChange(reducedMotionMq, updateMotionButtons);

  // Rotating a phone or resizing changes how many rows may play at once.
  window.addEventListener('resize', schedulePreviews, { passive: true });
  // Previews start once the page can be used (see "When a preview starts").
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', schedulePreviews, { once: true });

  window.addEventListener('hashchange', renderDebug);
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', renderDebug, { once: true });
  else renderDebug();

  // The theme replaces or extends the product grid (filters, "Load more").
  window.addEventListener('resize', scheduleReels, { passive: true });
  doc.addEventListener('af:grid-updated', scheduleReels);
  scheduleReels();

  // Preview videos outside the custom elements (none by default) still autoplay.
  doc.querySelectorAll('video[data-af-vs-autoplay]').forEach(registerPreview);

  // A fresh load onto the player's own history entry (reload, or Back without
  // bfcache) leaves a same-URL entry that makes the next Back look dead. It was
  // pushed from this same page, so stepping over it is a same-document
  // traversal and nothing visibly changes.
  try {
    if (history.state && history.state.afVs === true && !player.isOpen()) history.back();
  } catch (e) {
    /* ignore */
  }

  window.AfVideoShop = {
    open(rootOrPayload, startIndex) {
      const payload =
        rootOrPayload && rootOrPayload.items ? rootOrPayload : getPayload(rootOrPayload);
      if (payload) player.open(payload, startIndex || 0, doc.activeElement);
    },
    close: () => player.close(),
    isOpen: () => player.isOpen(),
    initScroller,
    registerPreview,
    unregisterPreview,
    placeReels,
  };
})();
